import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getInvite, acceptInvite, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setError("Missing invitation token."); setLoading(false); return; }
    getInvite(token)
      .then((d) => setInvite(d))
      .catch((e) => setError(apiError(e)))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      const data = await acceptInvite(token, name.trim(), password);
      setUser(data);
      toast.success("Welcome to the team!");
      nav("/dashboard");
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <AuthShell>
      <div data-testid="accept-invite-page">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : error ? (
          <div className="text-center py-8" data-testid="invite-error">
            <XCircle className="w-12 h-12 text-rose-500 mx-auto" />
            <h2 className="font-display text-xl font-bold text-[#0B132B] mt-4">Invitation unavailable</h2>
            <p className="text-slate-500 text-sm mt-2">{error}</p>
            <Link to="/login" className="inline-block mt-6 text-[#1E7A6B] font-semibold text-sm hover:underline">Go to sign in</Link>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-[#1E7A6B] text-sm font-semibold"><CheckCircle2 className="w-4 h-4" /> Team Invitation</div>
            <h2 className="font-display text-2xl font-bold text-[#0B132B] mt-2">Join {invite.business_name}</h2>
            <p className="text-slate-500 text-sm mt-1">
              You've been invited{invite.invited_by_name ? ` by ${invite.invited_by_name}` : ""} as{" "}
              <span className="font-semibold text-[#0B132B]">{invite.role.charAt(0).toUpperCase() + invite.role.slice(1)}</span>. Set your password to continue.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label>Email</Label>
                <Input value={invite.email} disabled data-testid="invite-email-display" />
              </div>
              <div>
                <Label htmlFor="ai-name">Full name</Label>
                <Input id="ai-name" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Your name" data-testid="accept-name-input" />
              </div>
              <div>
                <Label htmlFor="ai-pass">Create password</Label>
                <Input id="ai-pass" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} placeholder="At least 8 characters" data-testid="accept-password-input" />
              </div>
              <Button type="submit" disabled={busy} className="w-full bg-[#1E7A6B] hover:bg-[#186052] text-white" data-testid="accept-submit-btn">
                {busy ? "Joining…" : "Accept & Join Team"}
              </Button>
            </form>
          </>
        )}
      </div>
    </AuthShell>
  );
}
